import {
  MenuListingsLayout,
  PublicTermStatus,
  useActiveIncentives,
  useMealSummaries,
} from '@tovala/browser-apis-combinedapi'
import { ArrowLeftIcon, Button } from '@tovala/component-library'
import { events } from 'analytics/events'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import Link from 'components/common/Link'
import { useUser } from 'contexts/user'
import { useDetailsDialog } from 'components/common/details/hooks'
import { useMealsSignUp } from 'hooks/mealsSignUp'
import { flatMap } from 'lodash-es'
import { useNavigate } from 'react-router-dom'
import { track } from 'utils/analytics'
import { DATE_FORMATS, formatDate } from 'utils/dates'
import { getTotalCreditsCents } from 'utils/user'
import CreditsBanner from './CreditsBanner'
import CustomerIOInlineContainer from 'components/common/CustomerIOInlineContainer'
import ListingDetailsDialog from 'components/common/details/ListingDetailsDialog'
import ExtrasGrid from './ExtrasGrid'
import MenuComponentsGrid from './MenuComponentsGrid'
import MenuFixedBottomContainer from './MenuFixedBottomContainer'
import MenuMealDetailsDialog from './MenuMealDetailsDialog'
import MenuTabGroup, { useMenuTabs } from './MenuTabGroup'
import MenuUnavailable from './MenuUnavailable'
import StatusDot from './StatusDot'
import {
  useMenuProductRatingAverages,
  useMenuProductRatings,
  useResolvedMenuComponents,
} from './hooks'
import { useFeatures } from 'contexts/features'
import MenuError from './MenuError'
import PromosCreditsModal from '../PromosCreditsModal'

const InactiveSubscriptionMenu = ({
  menuListingsLayout,
  selectedPublicTerm,
}: {
  menuListingsLayout: MenuListingsLayout
  selectedPublicTerm: PublicTermStatus
}) => {
  const { user } = useUser()

  const menuTabs = useMenuTabs()

  const {
    closeDetailsDialog,
    detailsType,
    listing,
    meal,
    openDetailsDialog,
    shouldScrollToReview,
  } = useDetailsDialog({
    listings: flatMap(menuListingsLayout.sections, (section) => {
      return section ? section.listings : []
    }),
    termID: selectedPublicTerm.termID,
  })

  const menuID = selectedPublicTerm.subTerms[0].mainMenu.id
  const { ratingsByMealID } = useMenuProductRatings({ menuID })
  const { averageRatingsByMealID } = useMenuProductRatingAverages({ menuID })

  const { data: mealSummaries = [] } = useMealSummaries({
    subTermID: selectedPublicTerm.selectedSubTermID,
  })

  const features = useFeatures()

  const { loadMenuError, resolvedMenuComponents } = useResolvedMenuComponents({
    enableMenuTargeting: features.enableMenuTargeting,
    mealSelectionIDs: [],
    listings: menuListingsLayout.sections.flatMap(
      (section) => section?.listings,
    ),
    mealSummaries,
    mealSwaps: selectedPublicTerm.subTerms[0].mainMenu.mealSwaps,
    orderAgainMeals: [],
    ratingsByMealID,
    subTermID: selectedPublicTerm.subTerms[0].subTermID,
    userID: user.id,
  })

  const { link } = useMealsSignUp({ utmSource: 'menu' })

  const { data: activeIncentives = [] } = useActiveIncentives({
    userID: user.id,
  })

  const [showPromoCreditsModal, setShowPromoCreditsModal] = useState(false)

  const commonMenuComponentGridProps = {
    averageRatingsByMealID,
    canDecrement: false,
    canIncrement: false,
    canModifySelections: false,
    listingsSelections: [],
    mealSelectionIDs: [],
    onClickDecrement: () => {
      // Pass
    },
    onClickIncrement: () => {
      // Pass
    },
    onClickListing: (listingID: string, coupledMealID?: number) => {
      openDetailsDialog({ type: 'listing', id: listingID })

      if (coupledMealID !== undefined) {
        track(events.OPENS_EXTRA_DETAIL, {
          listing_id: listingID,
          coupled_meal_id: coupledMealID,
        })
      }
    },
    onClickMeal: (
      mealID: number,
      opts?: { shouldScrollToReview?: boolean },
    ) => {
      openDetailsDialog({
        type: 'meal',
        id: mealID,
        rating: ratingsByMealID.get(mealID)?.stars ?? null,
        shouldScrollToReview: opts?.shouldScrollToReview,
      })
    },
    ratingsByMealID,
    termID: selectedPublicTerm.termID,
  }

  const menuHasListings =
    menuListingsLayout.sections &&
    menuListingsLayout.sections.some((section) => section?.listings.length > 0)

  useEffect(() => {
    track(events.OPENS_MENU, {
      term_id: selectedPublicTerm.termID,
      meal_plan_state: 'no_plan',
      meal_selection_count: 0,
      meal_selection_max: 0,
      enabled_filters: '',
    })
  }, [selectedPublicTerm.termID])

  return (
    <>
      {selectedPublicTerm.readyForView ? (
        <>
          {loadMenuError ? (
            <MenuError
              menuHeader={
                <InactiveSubscriptionMenuHeader
                  isHeaderSticky={false}
                  startDate={selectedPublicTerm.startDate}
                />
              }
              subTermID={selectedPublicTerm.subTerms[0].subTermID}
              userID={user.id}
            />
          ) : (
            <MenuTabGroup
              commonMenuContent={
                <div className="mx-auto mt-4 max-w-menu md:px-4">
                  <CustomerIOInlineContainer
                    elementID={`cio-menu-term-${selectedPublicTerm.termID}`}
                  />
                </div>
              }
              menuHeader={({ isHeaderSticky }) => {
                return (
                  <InactiveSubscriptionMenuHeader
                    isHeaderSticky={isHeaderSticky}
                    startDate={selectedPublicTerm.startDate}
                  />
                )
              }}
              menuTabPanels={{
                meals: (
                  <div className="pb-52 lg:px-4 md:px-0 md:pb-40">
                    <MenuComponentsGrid
                      {...commonMenuComponentGridProps}
                      components={resolvedMenuComponents}
                    />
                  </div>
                ),
                extras: (
                  <div className="pb-52 lg:px-4 md:px-0 md:pb-40">
                    {menuHasListings ? (
                      <ExtrasGrid
                        canModifySelections={false}
                        disabledDecrement={true}
                        disabledIncrement={true}
                        listingsSelections={[]}
                        menuListingsLayout={menuListingsLayout}
                        onClickDecrement={() => {
                          // Pass
                        }}
                        onClickIncrement={() => {
                          // Pass
                        }}
                        onClickListing={(listingID: string) => {
                          openDetailsDialog({
                            id: listingID,
                            type: 'listing',
                          })

                          track(events.OPENS_EXTRA_DETAIL, {
                            listing_id: listingID,
                          })
                        }}
                      />
                    ) : (
                      <div className="md:px-4">
                        <div className="mx-auto mt-16 max-w-[512px] rounded-lg bg-white px-10 py-4 md:px-4">
                          <div className="space-y-4 text-center md:text-left">
                            <h2 className="text-k/20_110">
                              No Extras This Week
                            </h2>
                            <p className="text-k/16_125 text-grey-9 md:text-body-sm">
                              Although you can usually find extras here, there
                              are none this week. Please check on another menu
                              to find extras.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ),
              }}
              menuTabs={menuTabs}
              tabChangeEventProps={{
                maxSelections: -1,
                selectionsCount: -1,
                termID: selectedPublicTerm.termID,
              }}
            />
          )}
        </>
      ) : (
        <MenuUnavailable
          menuHeader={
            <InactiveSubscriptionMenuHeader
              isHeaderSticky={false}
              startDate={selectedPublicTerm.startDate}
            />
          }
        />
      )}

      <MenuFixedBottomContainer
        banner={
          <CreditsBanner
            activeIncentives={activeIncentives}
            isFreeDessertCustomer={user.isFreeDessertCustomer}
            onClickBanner={() => setShowPromoCreditsModal(true)}
            totalCreditsCents={getTotalCreditsCents({ user })}
          />
        }
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="h-4 w-4 md:h-3 md:w-3">
              <StatusDot color="orange" />
            </div>

            <span className="text-k/20_110">No Meal Plan</span>
          </div>

          <div>
            <Link
              linkStyle="dark"
              linkType={link.type}
              onClick={() => {
                track(events.TAPPED_MENU_CTA_GET_STARTED)
              }}
              size="large"
              to={link.to}
            >
              Sign Up
            </Link>
          </div>
        </div>
      </MenuFixedBottomContainer>

      {detailsType === 'meal' && meal ? (
        <MenuMealDetailsDialog
          averageStars={averageRatingsByMealID.get(meal.id)}
          closeModal={closeDetailsDialog}
          meal={meal}
          rating={ratingsByMealID.get(meal.id)}
          shouldScrollToReview={shouldScrollToReview}
        />
      ) : detailsType === 'listing' && listing ? (
        <ListingDetailsDialog listing={listing} onClose={closeDetailsDialog} />
      ) : null}
      {showPromoCreditsModal && (
        <PromosCreditsModal
          onCloseModal={() => setShowPromoCreditsModal(false)}
          user={user}
        />
      )}
    </>
  )
}

export default InactiveSubscriptionMenu

const InactiveSubscriptionMenuHeader = ({
  isHeaderSticky,
  startDate,
}: {
  isHeaderSticky: boolean
  startDate: string
}) => {
  const navigate = useNavigate()

  return (
    <div className="flex items-center lg:px-4">
      <div className="absolute">
        <Button
          aria-label="My Orders"
          buttonStyle="link"
          onClick={() => {
            navigate('/my-orders')

            track(events.MENU_BACK_CTA)
          }}
          size="auto"
        >
          <div className="mr-2 h-6 w-6">
            <ArrowLeftIcon />
          </div>
          <span className="text-k/16_125 md:hidden">My Orders</span>
        </Button>
      </div>

      <h1
        className={clsx('w-full text-center md:text-k/28_130', {
          'text-k/36_110': isHeaderSticky,
          'text-k/52_110': !isHeaderSticky,
        })}
      >
        {formatDate(startDate, {
          format: DATE_FORMATS.MONTH_ABBR_DAY,
        })}
      </h1>
    </div>
  )
}
